<template>
  <div class="section">
    <div class="toolbar">
      <div class="toolbar-filters">
        <label class="field-inline">
          <span>Тип проблемы</span>
          <select v-model="filters.problem_type" class="select" @change="applyFilters">
            <option value="">все нерешённые</option>
            <option v-for="option in PROBLEM_TYPES" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="field-inline">
          <span>ID контакта</span>
          <input
            v-model.number="filters.contact_id"
            type="number"
            min="1"
            class="filter-input"
            placeholder="все"
            @keyup.enter="applyFilters"
          >
        </label>
        <button type="button" class="btn-secondary btn-apply" :disabled="loading" @click="applyFilters">Применить</button>
      </div>
      <div class="toolbar-meta">
        <span class="hint">Найдено: {{ filteredTotal }} из {{ total }} · автообновление каждые 30с</span>
        <button type="button" class="btn-secondary" :disabled="loading" @click="loadProblems">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
      </div>
    </div>

    <div v-if="hasError" class="error-message" role="alert">Не удалось загрузить проблемы публикации.</div>
    <div
      v-if="message"
      :class="messageType === 'error' ? 'error-message' : 'info-message'"
      :role="messageType === 'error' ? 'alert' : 'status'"
    >{{ message }}</div>

    <div class="table-wrap">
      <div v-if="loading && items.length > 0" class="refresh-bar" aria-hidden="true" />
      <my-loader v-if="loading && items.length === 0" class="table-loader" />
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Заявки / замена</th>
            <th>Маршрут</th>
            <th>Контакт</th>
            <th>Этап / статус</th>
            <th>Причина</th>
            <th>Попытки</th>
            <th>Обновлено / повтор</th>
            <th class="col-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="items.length === 0">
            <td :colspan="8" class="empty-cell">Нерешённых проблем не найдено</td>
          </tr>
          <tr v-for="item in items" :key="rowKey(item)">
            <td data-label="Заявки / замена">
              <div v-if="item.replacement" class="replacement-refs">
                <span class="uid mono" :title="item.replacement.replacement_key">
                  Замена: {{ shortKey(item.replacement.replacement_key) }}
                </span>
                <span class="app-ids">
                  ATI: {{ item.replacement.victim_app_id }} → {{ item.replacement.published_app_id || '—' }}
                </span>
              </div>
              <div v-if="item.applications && item.applications.length" class="uid-list">
                <template v-for="application in item.applications" :key="applicationKey(application)">
                  <router-link
                    v-if="hasSourceId(application)"
                    class="application-link uid mono"
                    :to="{ name: 'Distribution', query: { tripId: application.source_id } }"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ application.uid }}</router-link>
                  <span v-else class="uid mono">{{ application.uid }}</span>
                </template>
              </div>
              <div v-else-if="item.member_uids && item.member_uids.length" class="uid-list">
                <span v-for="uid in item.member_uids" :key="uid" class="uid mono">{{ uid }}</span>
              </div>
              <span v-else-if="!item.replacement">—</span>
            </td>
            <td data-label="Маршрут" class="mono">{{ routeLabel(item) }}</td>
            <td data-label="Контакт" class="mono">{{ item.contact_id || '—' }}</td>
            <td data-label="Этап / статус">
              <div class="status-cell">
                <span>{{ stageLabel(item.stage) }}</span>
                <span class="badge" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
              </div>
            </td>
            <td data-label="Причина" class="reason-cell">
              <strong>{{ problemTypeLabel(item.problem_type) }}</strong>
              <span :title="item.reason_message">{{ item.reason_message || '—' }}</span>
            </td>
            <td data-label="Попытки" class="attempts">{{ item.attempt_count || 0 }}</td>
            <td data-label="Обновлено / повтор" class="date-cell">
              <span>{{ formatDateTime(item.updated_at) }}</span>
              <span :class="{ overdue: isOverdue(item.next_retry_at) }">
                Повтор: {{ formatDateTime(item.next_retry_at) }}
              </span>
            </td>
            <td data-label="Действия" class="col-actions">
              <button
                v-if="item.failure_key"
                type="button"
                class="btn-retry"
                :disabled="Boolean(retryingKey)"
                @click="retryFailure(item)"
              >{{ retryingKey === item.failure_key ? 'Повторяем...' : 'Повторить' }}</button>
              <span v-else class="no-action">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getProblems, retryFailure as retryFailureRequest } from '@/backend/distributorRoutingApi'

const POLL_INTERVAL = 30000
const PROBLEM_TYPES = [
  { value: 'preflight_failed', label: 'Ошибка проверки' },
  { value: 'unassigned', label: 'Не распределено' },
  { value: 'suppressed', label: 'Не опубликовано' },
  { value: 'failed', label: 'Ошибка публикации' },
  { value: 'retry_wait', label: 'Ожидает повтора' },
  { value: 'unknown', label: 'Неизвестное состояние' },
  { value: 'active_failure', label: 'Активная ошибка ATI' },
  { value: 'replacement', label: 'Ошибка замены публикации' },
]

const TYPE_LABELS = Object.fromEntries(PROBLEM_TYPES.map((item) => [item.value, item.label]))
const STATUS_LABELS = {
  failed: 'Ошибка',
  retry_wait: 'Ожидает повтора',
  unknown: 'Неизвестно',
  suppressed: 'Не опубликовано',
  unassigned: 'Не распределено',
  retryable: 'Можно повторить',
  terminal: 'Без повтора',
  passed: 'Проверено',
  publish_unknown: 'Публикация не подтверждена',
  delete_retry_wait: 'Удаление ожидает повтора',
  cleanup_blocked: 'Очистка заблокирована',
  compensation_blocked: 'Восстановление публикации заблокировано',
}
const STAGE_LABELS = {
  preflight: 'Проверка',
  assignment: 'Распределение',
  publication: 'Публикация',
  create: 'Создание',
  update: 'Обновление',
  delete: 'Удаление',
  replacement: 'Замена публикации',
}

export default {
  name: 'ProblemsSection',
  props: {
    active: { type: Boolean, default: false },
  },
  emits: ['total'],
  setup(props, { emit }) {
    const store = useStore()
    const items = ref([])
    const total = ref(0)
    const filteredTotal = ref(0)
    const loading = ref(false)
    const hasError = ref(false)
    const message = ref('')
    const messageType = ref('info')
    const retryingKey = ref('')
    const filters = reactive({ problem_type: '', contact_id: null })
    let pollTimer = null
    let requestInFlight = false
    let refreshQueued = false
    let disposed = false
    let filtersVersion = 0

    const actor = computed(() => {
      const user = store.state.user?.user || {}
      return String(user.email || user.name || (user.id ? `erp-user-${user.id}` : 'operator')).slice(0, 100)
    })

    const clearPoll = () => {
      if (pollTimer) clearTimeout(pollTimer)
      pollTimer = null
    }

    const schedulePoll = () => {
      clearPoll()
      if (!disposed && props.active) pollTimer = setTimeout(loadProblems, POLL_INTERVAL)
    }

    const requestParams = () => {
      const params = { limit: 500 }
      if (filters.problem_type) params.problem_type = filters.problem_type
      if (Number(filters.contact_id) > 0) params.contact_id = Number(filters.contact_id)
      return params
    }

    const loadProblems = async () => {
      if (requestInFlight) {
        refreshQueued = true
        return
      }
      requestInFlight = true
      loading.value = true
      const requestFiltersVersion = filtersVersion
      clearPoll()
      try {
        const data = await getProblems(requestParams())
        if (disposed) return
        if (requestFiltersVersion === filtersVersion) {
          items.value = Array.isArray(data?.items) ? data.items : []
          total.value = Number(data?.total) || 0
          filteredTotal.value = Number(data?.filtered_total ?? data?.items?.length) || 0
          emit('total', total.value)
          hasError.value = false
        }
      } catch {
        if (!disposed && requestFiltersVersion === filtersVersion) hasError.value = true
      } finally {
        requestInFlight = false
        if (!disposed) loading.value = false
        if (refreshQueued && !disposed) {
          refreshQueued = false
          loadProblems()
        } else {
          schedulePoll()
        }
      }
    }

    const applyFilters = () => {
      message.value = ''
      filtersVersion += 1
      loadProblems()
    }

    const retryFailure = async (item) => {
      if (!item.failure_key) return
      if (!window.confirm('Повторить операцию публикации? Ошибка будет сброшена и операция вернётся в очередь.')) return
      retryingKey.value = item.failure_key
      message.value = ''
      try {
        await retryFailureRequest(item.failure_key, {
          actor: actor.value,
          reason: 'Ручной повтор оператором из ERP',
        })
        message.value = 'Операция возвращена в очередь на повтор.'
        messageType.value = 'info'
        await loadProblems()
      } catch (error) {
        message.value = extractError(error, 'Не удалось повторить операцию.')
        messageType.value = 'error'
      } finally {
        retryingKey.value = ''
      }
    }

    const extractError = (error, fallback) => {
      if (typeof error.response?.data?.detail === 'string') return error.response.data.detail
      return fallback
    }

    const formatDateTime = (value) => {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
    }

    const isOverdue = (value) => value && new Date(value).getTime() < Date.now()
    const problemTypeLabel = (value) => TYPE_LABELS[value] || value || 'Проблема'
    const statusLabel = (value) => STATUS_LABELS[value] || value || '—'
    const stageLabel = (value) => STAGE_LABELS[value] || value || '—'
    const routeLabel = (item) => (
      item.from_city_id && item.to_city_id
        ? `ID ${item.from_city_id} → ID ${item.to_city_id}`
        : item.direction_key || '—'
    )
    const rowKey = (item) => [
      item.failure_key || item.replacement?.replacement_key || item.assignment_key || item.direction_key || 'problem',
      item.problem_type,
      item.updated_at,
      (item.applications || []).map(applicationKey).join(','),
      (item.member_uids || []).join(','),
    ].join(':')
    const shortKey = (value) => {
      const key = String(value || '')
      return key.length > 18 ? `${key.slice(0, 18)}…` : key || '—'
    }
    const hasSourceId = (application) => (
      application.source_id !== null
      && application.source_id !== undefined
      && application.source_id !== ''
    )
    const applicationKey = (application) => `${application.uid}:${application.source_id || ''}`
    const statusClass = (status) => {
      if (['retry_wait', 'retryable', 'delete_retry_wait'].includes(status)) return 'badge-wait'
      if (['failed', 'terminal', 'cleanup_blocked'].includes(status)) return 'badge-failed'
      if (['unknown', 'publish_unknown'].includes(status)) return 'badge-unknown'
      return 'badge-muted'
    }

    watch(() => props.active, (active) => {
      clearPoll()
      if (active) loadProblems()
    })
    onMounted(loadProblems)
    onBeforeUnmount(() => {
      disposed = true
      clearPoll()
    })

    return {
      PROBLEM_TYPES, items, total, filteredTotal, loading, hasError, message, messageType,
      retryingKey, filters, loadProblems, applyFilters, retryFailure, formatDateTime, isOverdue,
      problemTypeLabel, statusLabel, stageLabel, routeLabel, rowKey, statusClass,
      shortKey, hasSourceId, applicationKey,
    }
  },
}
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.toolbar-filters { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.toolbar-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; margin-left: auto; }
.field-inline { display: flex; flex-direction: column; gap: 3px; color: #475569; font-size: 11px; }
.filter-input, .select { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; font-size: 13px; height: 36px; padding: 8px 10px; }
.filter-input { width: 120px; }
.select { width: 210px; }
.hint { color: #98a2b3; font-size: 11px; }
.table-wrap { min-height: 180px; overflow-x: auto; position: relative; }
:deep(.table-loader.loading) { height: 180px; }
.data-table { border-collapse: collapse; font-size: 13px; min-width: 1100px; width: 100%; }
.data-table th, .data-table td { border-bottom: 1px solid #edf0f3; padding: 9px 10px; text-align: left; vertical-align: middle; }
.data-table th { background: #f8fafc; color: #475569; font-size: 11px; font-weight: 800; white-space: nowrap; }
.data-table tbody tr:hover { background: #f3f6fb; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.uid-list, .replacement-refs, .status-cell, .reason-cell, .date-cell { display: flex; flex-direction: column; gap: 4px; }
.uid { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.application-link { color: #2563eb; text-decoration: none; }
.application-link:hover { text-decoration: underline; }
.app-ids { color: #64748b; font-size: 11px; }
.reason-cell { max-width: 340px; min-width: 220px; }
.reason-cell > span:last-child { line-height: 1.35; }
.date-cell { white-space: nowrap; }
.date-cell span:last-child { color: #64748b; font-size: 11px; }
.date-cell .overdue { color: #b91c1c; font-weight: 700; }
.attempts { text-align: center !important; }
.badge { align-self: flex-start; border-radius: 8px; display: inline-flex; font-size: 11px; font-weight: 700; padding: 4px 8px; }
.badge-failed { background: #fee2e2; color: #991b1b; }
.badge-wait { background: #fff7ed; color: #c2410c; }
.badge-unknown { background: #e8eefc; color: #2563eb; }
.badge-muted { background: #f1f5f9; color: #475569; }
.empty-cell { color: #6b7280; padding: 28px !important; text-align: center !important; }
.data-table th.col-actions, .data-table td.col-actions { text-align: right; white-space: nowrap; }
.no-action { color: #94a3b8; }
.error-message { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
.info-message { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 10px; color: #1d4ed8; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
button { border: none; border-radius: 8px; box-sizing: border-box; cursor: pointer; font-size: 12px; font-weight: 700; height: 36px; padding: 0 14px; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.toolbar-meta .btn-secondary { min-width: 150px; }
.btn-retry { background: #eef4ff; color: #1d4ed8; height: 30px; padding: 0 10px; }
.refresh-bar { height: 2px; left: 0; overflow: hidden; position: absolute; right: 0; top: 0; z-index: 2; }
.refresh-bar::after { animation: refresh 1.2s ease-in-out infinite; background: #2563eb; content: ''; height: 100%; left: -35%; position: absolute; width: 35%; }
@keyframes refresh { to { left: 100%; } }

@media (max-width: 760px) {
  .toolbar, .toolbar-filters { align-items: stretch; flex-direction: column; }
  .toolbar-meta { align-items: stretch; margin-left: 0; }
  .filter-input, .select, .btn-apply { width: 100%; }
  .table-wrap { overflow: visible; }
  .data-table { min-width: 0; }
  .data-table thead { display: none; }
  .data-table tbody, .data-table tr, .data-table td { display: block; width: 100%; }
  .data-table tr { border: 1px solid #e5e7eb; border-radius: 10px; box-sizing: border-box; margin-bottom: 12px; overflow: hidden; }
  .data-table td { align-items: flex-start; border-bottom: 1px solid #edf0f3; box-sizing: border-box; display: grid; gap: 10px; grid-template-columns: 105px minmax(0, 1fr); padding: 9px 10px; text-align: left !important; white-space: normal; }
  .data-table td::before { color: #64748b; content: attr(data-label); font-size: 10px; font-weight: 800; text-transform: uppercase; }
  .data-table td:last-child { border-bottom: none; }
  .data-table .empty-cell { display: block; }
  .data-table .empty-cell::before { content: none; }
  .reason-cell { max-width: none; min-width: 0; }
  .uid { max-width: 100%; }
}
</style>
